import type { ReactNode } from "react";
import { Bell, FileText, Key, Layers, MessageSquare } from "lucide-react";
import { StepInfoRow } from "../StepInfoRow";
import { getString } from "./helpers";
import type { SummaryTranslator } from "./types";

function getObject(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

export function getNotifySummary(
  config: Record<string, unknown>,
  t: SummaryTranslator,
): ReactNode[] {
  const items: ReactNode[] = [];

  const channel = getString(config.channel) || "lark";
  const larkSendMethod = getString(config.larkSendMethod) || getString(config.sendMethod);
  const templateResourceId = getString(config.templateResourceId);
  const cardFromRowField = getString(config.cardFromRowField);
  const webhookResourceName = getString(config.webhookResourceName);
  const botResourceName = getString(config.botResourceName);
  const userAuthResourceName = getString(config.userAuthResourceName);
  const inlineTemplate = getString(config.inlineTemplate);
  const telegram = getObject(config.telegram);
  const telegramChatId = getString(telegram?.chatId);

  // Channel names are product names — kept verbatim
  const channelLabels: Record<string, { label: string; color: string }> = {
    lark: {
      label: "Lark",
      color: "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300",
    },
    slack: {
      label: "Slack",
      color: "bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300",
    },
    telegram: {
      label: "Telegram",
      color: "bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300",
    },
    email: {
      label: "Email",
      color: "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300",
    },
  };
  const channelInfo = channelLabels[channel] || channelLabels.lark;

  items.push(
    <StepInfoRow
      key="channel"
      icon={<Bell className="size-3.5" />}
      label={t("summaries.notify.label_channel")}
      value={
        <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${channelInfo.color}`}>
          {channelInfo.label}
        </span>
      }
    />,
  );

  if (channel === "lark" && larkSendMethod) {
    const methodLabels: Record<string, string> = {
      webhook: t("summaries.notify.method_webhook"),
      bot: t("summaries.notify.method_bot"),
      user: t("summaries.notify.method_user"),
    };
    items.push(
      <StepInfoRow
        key="sendMethod"
        icon={<MessageSquare className="size-3.5" />}
        label={t("summaries.notify.label_method")}
        value={methodLabels[larkSendMethod] || larkSendMethod}
      />,
    );

    let resourceNameValue: string | undefined;
    let resourceLabel: string | undefined;
    if (larkSendMethod === "webhook" && webhookResourceName) {
      resourceNameValue = webhookResourceName;
      resourceLabel = t("summaries.notify.resource_webhook");
    } else if (larkSendMethod === "bot" && botResourceName) {
      resourceNameValue = botResourceName;
      resourceLabel = t("summaries.notify.resource_bot");
    } else if (larkSendMethod === "user" && userAuthResourceName) {
      resourceNameValue = userAuthResourceName;
      resourceLabel = t("summaries.notify.resource_auth");
    }

    if (resourceNameValue) {
      items.push(
        <StepInfoRow
          key="larkResource"
          icon={<Key className="size-3.5" />}
          label={resourceLabel || t("summaries.notify.resource_label")}
          value={resourceNameValue}
        />,
      );
    }
  }

  if (channel === "telegram") {
    items.push(
      <StepInfoRow
        key="telegramMethod"
        icon={<MessageSquare className="size-3.5" />}
        label={t("summaries.notify.label_method")}
        value={t("summaries.notify.method_bot")}
      />,
    );
    if (telegramChatId) {
      items.push(
        <StepInfoRow
          key="telegramChat"
          icon={<Key className="size-3.5" />}
          label={t("summaries.notify.label_target_chat")}
          value={telegramChatId}
          mono
        />,
      );
    }
  }

  if (cardFromRowField) {
    items.push(
      <StepInfoRow
        key="cardFromRow"
        icon={<Layers className="size-3.5" />}
        label={t("summaries.notify.label_card_source")}
        value={t("summaries.notify.card_source_value", { field: cardFromRowField })}
        mono
      />,
    );
  } else if (templateResourceId) {
    items.push(
      <StepInfoRow
        key="template"
        icon={<FileText className="size-3.5" />}
        label={t("summaries.notify.label_template")}
        value={t("summaries.notify.template_configured")}
      />,
    );
  } else if (inlineTemplate) {
    items.push(
      <StepInfoRow
        key="template"
        icon={<FileText className="size-3.5" />}
        label={t("summaries.notify.label_template")}
        value={t("summaries.notify.template_inline")}
      />,
    );
  }

  return items;
}
